/**
 * Google Contacts, read only.
 *
 * A tutor's address book already holds the parent's number. Typing it again
 * into Magbit is the kind of work this product exists to remove, so this
 * fetches the book and lets them tick the people to attach to a student.
 *
 * The scope is `contacts.readonly` and nothing more: Magbit never writes to a
 * tutor's address book, and the token stays in the browser rather than
 * reaching the API.
 */

const CLIENT_ID = '533169606964-mgvn99cbhqgvv3mm5h768u440kvj266b.apps.googleusercontent.com';
const SCOPE = 'https://www.googleapis.com/auth/contacts.readonly';
const PEOPLE_API = 'https://people.googleapis.com/v1';

interface TokenResponse {
  access_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

interface TokenClient {
  callback: (response: TokenResponse) => void;
  requestAccessToken(options?: { prompt?: string }): void;
}

export interface GoogleContact {
  /** Google's own id. Re-importing the same person updates rather than duplicates. */
  resourceName: string;
  name: string;
  phone: string;
  relation: string | null;
}

const state = {
  scriptPromise: null as Promise<void> | null,
  tokenClient: null as TokenClient | null,
  accessToken: null as string | null,
  expiresAt: 0,
  cache: null as GoogleContact[] | null
};

function loadIdentityScript(): Promise<void> {
  if (state.scriptPromise) return state.scriptPromise;
  state.scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[src*="accounts.google.com/gsi"]');
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load Google Identity Services'));
    document.head.appendChild(script);
  });
  return state.scriptPromise;
}

async function accessToken(): Promise<string> {
  if (state.accessToken && state.expiresAt > Date.now() + 10_000) return state.accessToken;
  await loadIdentityScript();
  const oauth2 = window.google?.accounts?.oauth2;
  if (!oauth2) throw new Error('Google Identity Services unavailable');
  state.tokenClient ??= oauth2.initTokenClient({
    client_id: CLIENT_ID,
    scope: SCOPE,
    callback: () => {}
  });
  return new Promise<string>((resolve, reject) => {
    state.tokenClient!.callback = (response: TokenResponse) => {
      if (response.error || !response.access_token) {
        reject(new Error(response.error_description ?? response.error ?? 'No token'));
        return;
      }
      state.accessToken = response.access_token;
      state.expiresAt = Date.now() + Number(response.expires_in ?? 3600) * 1000;
      resolve(response.access_token);
    };
    state.tokenClient!.requestAccessToken({ prompt: '' });
  });
}

interface Person {
  resourceName?: string;
  names?: { displayName?: string }[];
  phoneNumbers?: { value?: string }[];
  relations?: { type?: string }[];
}

export const GoogleContactsService = {
  /**
   * Every contact that has a phone number, paged through in full so a search
   * covers the whole book rather than the first page of it. A contact without
   * a number is of no use here and is skipped.
   */
  async list(force = false): Promise<GoogleContact[]> {
    if (state.cache && !force) return state.cache;
    const token = await accessToken();
    const people: GoogleContact[] = [];
    let pageToken = '';

    do {
      const params = new URLSearchParams({
        personFields: 'names,phoneNumbers,relations',
        pageSize: '500',
        sortOrder: 'FIRST_NAME_ASCENDING'
      });
      if (pageToken) params.set('pageToken', pageToken);

      const response = await fetch(`${PEOPLE_API}/people/me/connections?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => null) as
        { connections?: Person[]; nextPageToken?: string; error?: { message?: string } } | null;
      if (!response.ok) {
        throw new Error(data?.error?.message ?? `People API ${response.status}`);
      }

      for (const person of data?.connections ?? []) {
        const phone = person.phoneNumbers?.[0]?.value;
        if (!phone) continue;
        people.push({
          resourceName: person.resourceName ?? phone,
          name: person.names?.[0]?.displayName ?? phone,
          phone,
          relation: person.relations?.[0]?.type ?? null
        });
      }
      pageToken = data?.nextPageToken ?? '';
    } while (pageToken);

    state.cache = people;
    return people;
  },

  /** Dropped when the picker closes, so a stale book is never reused later. */
  clearCache(): void {
    state.cache = null;
  }
};
